import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './transcript-message.module.scss';
import Target from '@/assets/icons/target-04.svg?react';
import Documents from '@/assets/icons/file-06.svg?react';
import User from '@/assets/icons/user-01.svg?react';
import Icon from '@ant-design/icons';
import { formatToHTML } from '@/utils/helpers';
import clsx from 'clsx';
import TopicChip from '../topic-chip/topic-chip';
import { Link } from 'react-router-dom';
import { isBotTurn, TranscriptField } from './transcript-message.utils';

interface TranscriptMessageProps {
  field: TranscriptField;
  messageText: string;
  detectedKeywords?: string[];
  document_id?: number;
}

export function TranscriptMessage({
  field,
  messageText,
  detectedKeywords,
  document_id,
}: TranscriptMessageProps) {
  const { t } = useTranslation('conversations');
  const bot = isBotTurn(field);

  const keywords = detectedKeywords?.length ? detectedKeywords : undefined;
  const documentId = document_id || undefined;

  return (
    <Flex vertical className={styles.wrapper}>
      <Flex
        gap={15}
        data-testid="transcript-row"
        data-side={bot ? 'bot' : 'resident'}
        className={clsx(bot ? styles.botRow : styles.residentRow)}
      >
        <div
          data-testid="transcript-avatar"
          className={clsx(styles.avatar, bot ? styles.botAvatar : styles.residentAvatar)}
        >
          {/* The mockup initials the caller, but no name reaches us, so every resident would
              wear the same meaningless letter. */}
          {bot ? (
            'AI'
          ) : (
            <User
              data-testid="resident-avatar-icon"
              stroke={styles.residentAvatarIcon}
              width={16}
              height={16}
            />
          )}
        </div>
        <Flex vertical>
          <Flex
            vertical
            gap={8}
            className={clsx(
              styles.bubble,
              bot ? styles.botBubble : styles.residentBubble,
              keywords && styles.bubbleWithKeyword
            )}
          >
            <div
              className={styles.message}
              dangerouslySetInnerHTML={{ __html: formatToHTML(messageText) }}
            />

            {keywords && (
              <Flex
                justify="center"
                align="center"
                gap={4}
                className={styles.keywordDetectedContainer}
              >
                <Icon
                  component={() => (
                    <Target stroke={styles.targetColorIcon} width={16} height={16} />
                  )}
                />
                <p className={styles.keywordHeading}>{t('drawer.transcript.keywordDetected')}</p>
                {[...new Set(keywords)].map((element) => (
                  <TopicChip className={styles.keywordChip} title={element} key={element} />
                ))}
              </Flex>
            )}

            {documentId !== undefined && (
              <Flex justify="right" align="center" gap={4} className={styles.documentRow}>
                <Icon
                  component={() => (
                    <Documents stroke={styles.targetColorIcon} width={16} height={16} />
                  )}
                />
                <p className={styles.documentHeading}>{t('drawer.transcript.document')}</p>
                <Link to={`/documents/${documentId}`}>{documentId}</Link>
              </Flex>
            )}
          </Flex>
        </Flex>

      </Flex>
    </Flex>
  );
}
